import * as EmotionTheming from 'emotion-theming'
import * as React from 'react'
import * as ReactRouter from 'react-router-dom'

import * as Common from '@rushplay/common'
import * as I18n from '@rushplay/i18n'
import { lazy as loadable } from '@loadable/component'

import * as Theming from './theming'
import { Card } from './card'
import { HtmlContent } from './html-content'
import { NavigationLink, PageNavigation } from './page-navigation'

const NotFound = loadable(() => import('./not-found'))

export function TermsPage() {
  const i18n = I18n.useI18n()
  const [content, setContent] = React.useState(null)
  const params = ReactRouter.useParams()
  const page = params.slug
  const theme = EmotionTheming.useTheme()

  const contentFromRef = React.useCallback(
    node => {
      if (node) {
        const blob = new Blob(['\uFEFF', node.textContent], {
          type: 'text/plain',
        })
        setContent(URL.createObjectURL(blob))
      }
    },
    [page]
  )

  if (!i18n.hasTranslation(`terms-and-condition.${page}.content`)) {
    return <NotFound />
  }

  return (
    <Theming.Alternative>
      <Common.Box pt={0} flexGrow={[null, '1']} display={[null, 'flex']}>
        <Common.Box
          display="flex"
          backgroundColor="card-background"
          backgroundImage={[
            `linear-gradient(to bottom, rgba(0, 0, 0, 0) 250px, ${theme.colors['g-bg']} 575px),
              url(${theme['bg-pattern-stripe-small']}),
              linear-gradient(to bottom, ${theme.colors['page-gradient-start']} 150px,
              ${theme.colors['page-gradient-middle']} 250px, ${theme.colors['page-gradient-stop']} 575px)`,
            null,
            `linear-gradient(to bottom, rgba(0, 0, 0, 0) 250px, ${theme.colors['g-bg']} 575px),
              url(${theme['bg-pattern-stripe-large']}),
              linear-gradient(to bottom, ${theme.colors['page-gradient-start']} 150px,
              ${theme.colors['page-gradient-middle']} 250px, ${theme.colors['page-gradient-stop']} 575px)`,
          ]}
          backgroundRepeat="no-repeat"
          backgroundPosition="left top"
          p={[1, null, 4]}
          justifyContent="center"
          alignItems="flex-start"
          fontSize={2}
          minHeight="100%"
          flexGrow={[null, '1']}
        >
          <Card p={[2, 4]} width="100%" maxWidth={[null, null, null, '1000px']}>
            <Common.Box ref={contentFromRef}>
              <HtmlContent
                html={{
                  __html: i18n.translate(
                    `terms-and-condition.${page}.content`,
                    {
                      logoImageSrc: i18n.translate(
                        'terms-and-conditions.general.license-logo'
                      ),
                    }
                  ),
                }}
              />
            </Common.Box>
            <Common.Box textAlign="center" py="4">
              <Common.Text
                as="a"
                download={`boom-${page.replace(/\//g, '-')}`}
                href={content}
                textDecoration="underline"
                fontSize="16px"
              >
                {i18n.translate(`terms-and-conditions.download`)}
              </Common.Text>
            </Common.Box>
          </Card>
        </Common.Box>
      </Common.Box>
    </Theming.Alternative>
  )
}

export function TermsAndConditions() {
  const i18n = I18n.useI18n()

  return (
    <Common.Box
      flexGrow="1"
      maxWidth={['100vw', '100%']}
      pt={[1, 2]}
      display={[null, 'flex']}
      flexDirection={[null, 'column']}
    >
      <Common.Box>
        <PageNavigation
          items={
            <React.Fragment>
              <NavigationLink to="/terms-and-conditions/general">
                {i18n.translate('terms-and-conditions.nav.general')}
              </NavigationLink>

              {i18n.hasTranslation('terms-and-conditions.nav.your-account') && (
                <NavigationLink to="/terms-and-conditions/your-account">
                  {i18n.translate('terms-and-conditions.nav.your-account')}
                </NavigationLink>
              )}

              {i18n.hasTranslation(
                'terms-and-conditions.nav.welcome-offer'
              ) && (
                <NavigationLink to="/terms-and-conditions/welcome-offer">
                  {i18n.translate('terms-and-conditions.nav.welcome-offer')}
                </NavigationLink>
              )}

              {i18n.hasTranslation(
                'terms-and-conditions.nav.loyalty-program'
              ) && (
                <NavigationLink to="/terms-and-conditions/loyalty-program">
                  {i18n.translate('terms-and-conditions.nav.loyalty-program')}
                </NavigationLink>
              )}
              {i18n.hasTranslation('terms-and-conditions.nav.sports') && (
                <NavigationLink to="/terms-and-conditions/sports">
                  {i18n.translate('terms-and-conditions.nav.sports')}
                </NavigationLink>
              )}
            </React.Fragment>
          }
          header="terms-and-conditions"
        />
      </Common.Box>
      <ReactRouter.Switch>
        <ReactRouter.Redirect
          exact
          from="/terms-and-conditions"
          to="terms-and-conditions/general"
        />
        <ReactRouter.Route
          path="/terms-and-conditions/:slug"
          component={TermsPage}
        />
      </ReactRouter.Switch>
    </Common.Box>
  )
}

export default TermsAndConditions
